import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import Menu from "../../comum/Menu";
import Footer from "../../comum/Footer";
import BarcodeModal from "../Components/BarcodeScanner";
import EtiquetaCard from "../Components/CardEtiqueta";
import { Modal, Button } from "react-bootstrap";
import Swal from "sweetalert2";

// --- Helpers ---
function calcularDistancia(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

const parseCurrencyToNumber = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return v;
    const s = String(v).replace(/\./g, "").replace(/,/g, ".");
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
};

const formatarMoeda = (valor) => {
    if (valor == null || valor === "") return "";
    const numero = parseCurrencyToNumber(valor);
    if (numero === 0) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
    }).format(numero);
};

const formatarData = (data) => {
    if (!data) return "";
    if (typeof data !== "string") return "";
    if (data.includes("/")) return data;
    const d = new Date(data);
    if (isNaN(d)) return data;
    const dia = String(d.getDate()).padStart(2, "0");
    const mes = String(d.getMonth() + 1).padStart(2, "0");
    const ano = d.getFullYear();
    return `${dia}/${mes}/${ano}`;
};

// --- Componente de Loading ---
function LoadingModal({ show, message }) {
    if (!show) return null;
    return (
        <div className="loading-modal-overlay">
            <div className="loading-modal-content">
                <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                >
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ marginTop: 15, fontSize: 18 }}>{message}</div>
            </div>
            <style>{`
                .loading-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1050;
                }
                .loading-modal-content {
                    background: white;
                    padding: 20px 40px;
                    border-radius: 8px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    box-shadow: 0 0 10px rgba(0,0,0,0.25);
                }
            `}</style>
        </div>
    );
}

// --- Componente principal ---
function EtiquetaProduto() {
    const [ean, setEan] = useState("");
    const [selectedEan, setSelectedEan] = useState(null);
    const [produto, setProduto] = useState(null);
    const [showScanner, setShowScanner] = useState(false);
    const [etiquetas, setEtiquetas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [showModal, setShowModal] = useState(false);

    // 🔒 Bloqueios
    const isScanningRef = useRef(false);
    const isEmittingRef = useRef(false); // NOVO: bloqueia múltiplas emissões

    const [lojaSelecionada, setLojaSelecionada] = useState(null);
    const [dentroDoRaio, setDentroDoRaio] = useState(false);

    const navigate = useNavigate();
    const chapa = localStorage.getItem("chapa");
    const usuario = chapa;

    // localizar loja automaticamente
    useEffect(() => {
        if (!chapa) {
            navigate("/arbor/login");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { latitude, longitude } = pos.coords;
                    const resp = await api.get("lojas/localizacao");
                    const lojas = resp.data || [];

                    const lojaProxima = lojas.find((loja) => {
                        const distKm = calcularDistancia(
                            latitude,
                            longitude,
                            parseFloat(loja.latitude),
                            parseFloat(loja.longitude)
                        );
                        const distMetros = distKm * 1000;
                        return distMetros <= parseFloat(loja.distancia || 0);
                    });

                    if (lojaProxima) {
                        setLojaSelecionada(lojaProxima);
                        setDentroDoRaio(true);
                    } else {
                        Swal.fire("Atenção", "Você não está dentro do raio de uma loja.", "warning");
                        setDentroDoRaio(false);
                    }
                } catch (err) {
                    console.error("Erro ao buscar lojas:", err);
                    Swal.fire("Erro", "Erro ao localizar lojas.", "error");
                    setDentroDoRaio(false);
                }
            },
            (err) => {
                console.error("Erro ao obter localização:", err);
                Swal.fire("Erro", "Não foi possível obter sua localização. Habilite o GPS.", "error");
                setDentroDoRaio(false);
            }
        );
    }, [chapa, navigate]);

    // buscar produto
    const buscarProduto = async (eanParam = null) => {
        const eanSearch = eanParam ?? ean;
        if (!eanSearch) return;

        try {
            setLoading(true);
            setLoadingMessage("Consultando produto...");
            setProduto(null);
            setShowModal(false);

            const resp = await api.get(
                `etiqueta?loja=${lojaSelecionada?.cod_emp}&ean=${encodeURIComponent(eanSearch)}`
            );
            const data = resp.data;

            if (data && data.sucesso === "n") {
                Swal.fire("Atenção", data.erro, "warning");
                return;
            }

            if (!data || Object.keys(data).length === 0) {
                Swal.fire("Atenção", "Produto não cadastrado.", "warning");
                return;
            }

            if (data.statusvenda !== "A") {
                Swal.fire("Atenção", "Produto bloqueado, entrar em contato com o comercial.", "warning");
                return;
            }

            setSelectedEan(eanSearch);
            setProduto({ ...data, ean: eanSearch });
            setShowModal(true);
            setEan("");
        } catch (err) {
            console.error(err);
            Swal.fire("Erro", "Erro ao buscar produto.", "error");
        } finally {
            setLoading(false);
        }
    };

    const emitirEtiqueta = async () => {
        // 🔒 BLOQUEIO ABSOLUTO CONTRA DUPLO DISPARO
        if (isEmittingRef.current) return;
        isEmittingRef.current = true;

        const eanToSend = selectedEan || produto?.ean;
        if (!eanToSend) {
            Swal.fire("Atenção", "EAN inválido para emissão.", "warning");
            isEmittingRef.current = false;
            return;
        }

        try {
            setLoading(true);
            setLoadingMessage("Emitindo etiqueta...");
            await api.post(
                `etiqueta?ean=${encodeURIComponent(eanToSend)}&loja=${lojaSelecionada?.cod_emp}&usuario=${usuario}`
            );
            Swal.fire("Sucesso", "Etiqueta enviada para fila com sucesso.", "success");
            await carregarEtiquetas();
            setProduto(null);
            setSelectedEan(null);
            setShowModal(false);
        } catch (err) {
            console.error(err);
            Swal.fire("Erro", "Erro ao incluir produto!", "error");
        } finally {
            setLoading(false);
            // 🔓 Libera emissão após pequeno delay de segurança
            setTimeout(() => {
                isEmittingRef.current = false;
            }, 1000);
        }
    };

    const carregarEtiquetas = async () => {
        try {
            setLoading(true);
            setLoadingMessage("Carregando fila de etiquetas...");
            const resp = await api.get(`etiqueta/lista?usuario=${usuario}`);
            const data = Array.isArray(resp.data) ? resp.data : [];
            setEtiquetas(data);
        } catch (err) {
            console.error(err);
            setEtiquetas([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (usuario) carregarEtiquetas();
    }, [usuario]);

    return (
        <div className="d-flex flex-column min-vh-100">
            <Menu modulo="ARBOR" />
            <div className="container-fluid flex-grow-1" style={{ paddingTop: '100px' }}>
                <div className="row justify-content-center">
                    <div className="col-lg-6">
                        <div className="mb-3">
                            <label>Loja</label>
                            <input
                                className="form-control"
                                value={lojaSelecionada?.razao || ""}
                                readOnly
                            />
                        </div>

                        <div className="mb-3">
                            <label>Código de barras</label>
                            <div className="input-group">
                                <input
                                    value={ean}
                                    onChange={(e) => setEan(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") buscarProduto();
                                    }}
                                    maxLength={30}
                                    type="text"
                                    className="form-control"
                                    placeholder="Digite ou escaneie"
                                    disabled={!dentroDoRaio || loading}
                                />
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={() => buscarProduto()}
                                    disabled={!dentroDoRaio || !ean || loading}
                                >
                                    <i className="fas fa-search"></i>
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    type="button"
                                    onClick={() => setShowScanner(true)}
                                    disabled={!dentroDoRaio || loading}
                                >
                                    <i className="fas fa-camera"></i>
                                </button>
                            </div>
                        </div>

                        <h4 className="mt-4">Fila de etiquetas</h4>
                        <div className="row">
                            {etiquetas.length > 0 ? (
                                etiquetas.map((etq) => (
                                    <EtiquetaCard key={etq.etq_sequencia} etq={etq} />
                                ))
                            ) : (
                                <div className="col-12 text-center text-muted mt-3">
                                    <i className="fas fa-info-circle fa-2x mb-2 text-secondary"></i>
                                    <p className="fw-bold">Nenhum produto na fila de impressão</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de Detalhes */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Detalhes do Produto</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {produto && (
                        <>
                            <img
                                src={`https://cdn-cosmos.bluesoft.com.br/products/${produto.ean}`}
                                alt={produto.desccompleta}
                                className="img-fluid mb-2"
                                style={{ maxHeight: "150px", objectFit: "contain" }}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://multilit.com.br/wp-content/uploads/2020/03/Produto-sem-foto.png";
                                }}
                            />
                            <div className="mb-2">
                                {(() => {
                                    const precoNum = parseCurrencyToNumber(produto.preco ?? produto.precovalidnormal ?? 0);
                                    const promoAppNum = parseCurrencyToNumber(produto.promoapp);
                                    const promoVarejoNum = parseCurrencyToNumber(produto.promovarejo);

                                    if (promoAppNum > 0) {
                                        return (
                                            <>
                                                <div style={{ textDecoration: "line-through" }} className="text-secondary">
                                                    {formatarMoeda(precoNum)}
                                                </div>
                                                <div className="fs-3 fw-bold text-success">
                                                    {formatarMoeda(promoAppNum)}
                                                </div>
                                            </>
                                        );
                                    }

                                    if (promoVarejoNum > 0) {
                                        return (
                                            <>
                                                <div style={{ textDecoration: "line-through" }} className="text-secondary">
                                                    {formatarMoeda(precoNum)}
                                                </div>
                                                <div className="fs-3 fw-bold text-primary">
                                                    {formatarMoeda(promoVarejoNum)}
                                                </div>
                                            </>
                                        );
                                    }

                                    return <div className="fs-3 fw-bold">{formatarMoeda(precoNum)}</div>;
                                })()}
                            </div>
                            <h5 className="card-title fw-bold">{produto.desccompleta}</h5>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="success"
                        onClick={emitirEtiqueta}
                        className="w-100"
                        disabled={loading}
                    >
                        Emitir Etiqueta
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* SCANNER COM LÓGICA DE BLOQUEIO */}
            <BarcodeModal
                show={showScanner}
                onClose={() => {
                    setShowScanner(false);
                    isScanningRef.current = false;
                }}
                onScanSuccess={(codigo) => {
                    if (isScanningRef.current) return;
                    isScanningRef.current = true;

                    setShowScanner(false);
                    setEan(codigo);

                    buscarProduto(codigo).finally(() => {
                        setTimeout(() => {
                            isScanningRef.current = false;
                        }, 1500);
                    });
                }}
            />

            <LoadingModal show={loading} message={loadingMessage} />
            <Footer />
        </div>
    );
}

export default EtiquetaProduto;
